import React, { useState, useEffect } from 'react';
import { apiService } from './services/api'; 
import UserManagementWidget from './widgets/UserManagementWidget'; 
// Import both CSS files
import './style/PageStyle/OrganisationManagement.css';
import './style/PageStyle/OrganisationUserTable.css'; 

const OrganisationManagement = ({ organisation, onClose, onSave, onDelete }) => {
  const [activeTab, setActiveTab] = useState('info'); 
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  
  // Organisation Form State
  const [formData, setFormData] = useState({
    name: '',
    customId: '',
    type: 'National Account',
    contactEmail: '',
    location: '',
    rebate: '',          // <--- ADDED
    redistribution: ''   // <--- ADDED
  });

  // User List State
  const [users, setUsers] = useState([]);
  
  // Widget Control State
  const [showUserWidget, setShowUserWidget] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); 

  const orgTypes = ['Head Office Account','National Account', 'Board', 'Supplier', 'Utilities', 'Wholesaler', 'Customer'];

  // --- INITIALIZATION ---
  useEffect(() => {
    if (organisation) {
      setFormData({
        name: organisation.name || '',
        customId: organisation.custom_id || '',
        type: organisation.type || 'National Account',
        contactEmail: organisation.contactEmail || '',
        location: organisation.location || '',
        rebate: organisation.rebate || '',                 // <--- ADDED
        redistribution: organisation.redistribution || ''  // <--- ADDED
      });
      fetchOrgUsers(organisation.id);
    }
  }, [organisation]);

  const fetchOrgUsers = async (orgId) => {
    setIsLoadingUsers(true);
    try {
      const data = await apiService.getUsersByOrganisation(orgId);
      setUsers(data);
    } catch (error) {
      console.error("Failed to load users", error);
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // --- ORG HANDLERS ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveInfo = async () => {
    try {
      // 1. Prepare the payload
      const payload = {
        name: formData.name,
        customId: formData.customId,
        type: formData.type,
        contactEmail: formData.contactEmail,
        location: formData.location,
        rebate: formData.rebate,                
        redistribution: formData.redistribution  
      };

      // 2. Call the API
      await apiService.updateOrganisation(organisation.id, payload);

      // 3. Notify parent to refresh the list
      if (onSave) {
        onSave({ ...organisation, ...payload });
      }
      
      // 4. Close or show success message
      alert("Organisation updated successfully!");
      onClose();

    } catch (error) {
      console.error("Failed to update organisation", error);
      alert(`Failed to update: ${error.message}`);
    }
  };

  // UPDATED FUNCTION
  const handleDeleteOrg = async () => {
    const confirmMessage = `WARNING: This will delete "${organisation.name}" and ALL ${users.length} users associated with it.\n\nAre you sure?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        // 1. Call API to delete Org and Users
        await apiService.deleteOrganisation(organisation.id);
        
        // 2. Notify parent component to remove from list
        if (onDelete) onDelete(organisation.id);
        
        // 3. Close the modal
        onClose();
      } catch (error) {
        console.error("Failed to delete organisation", error);
        alert(`Failed to delete organisation: ${error.message}`);
      }
    }
  };

  // --- USER HANDLERS ---
  const handleAddUserClick = () => {
    setSelectedUser(null); 
    setShowUserWidget(true);
  };

  const handleEditUserClick = (user) => {
    setSelectedUser(user); 
    setShowUserWidget(true);
  };

  const handleUserWidgetSave = async (userPayload) => {
    try {
      if (userPayload.id) {
        await apiService.updateUser(userPayload.id, userPayload);
      } else {
        await apiService.createUser(userPayload);
      }
      await fetchOrgUsers(organisation.id);
      setShowUserWidget(false);
    } catch (error) {
      console.error("User operation failed", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (window.confirm(`Remove user "${userName}"?`)) {
      try {
        await apiService.deleteUser(userId);
        await fetchOrgUsers(organisation.id);
      } catch (error) {
        console.error("Failed to delete user", error);
        alert("Failed to delete user.");
      }
    }
  };

  return (
    <>
      {/* MAIN ORGANISATION WIDGET */}
      <div className="widget-overlay" onClick={onClose}>
        <div className="widget-container large-modal" onClick={(e) => e.stopPropagation()}>
          
          <div className="widget-header">
            <h2>Manage: {organisation?.name}</h2>
            <button className="close-btn" onClick={onClose}>&times;</button>
          </div>

          <div className="org-tabs">
            <button 
              className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
            >
              ℹ️ Organisation Details
            </button>
            <button 
              className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              👥 User Management
            </button>
          </div>

          <div className="widget-content">
            
            {/* TAB 1: Organisation Info */}
            {activeTab === 'info' && (
              <div className="tab-content">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Organisation ID (Custom)</label>
                    <input 
                      type="text" 
                      name="customId" 
                      className="widget-input" 
                      value={formData.customId} 
                      onChange={handleInputChange} 
                    />
                  </div>
                  <div className="form-group">
                    <label>Organisation Name</label>
                    <input type="text" name="name" className="widget-input" value={formData.name} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Organisation Type</label>
                    <select name="type" className="widget-input" value={formData.type} onChange={handleInputChange}>
                      {orgTypes.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Contact Email</label>
                    <input type="email" name="contactEmail" className="widget-input" value={formData.contactEmail} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input type="text" name="location" className="widget-input" value={formData.location} onChange={handleInputChange} />
                  </div>
                  
                  {/* --- ADDED FIELDS --- */}
                  <div className="form-group">
                    <label>Rebate %</label>
                    <input type="number" step="0.01" name="rebate" className="widget-input" placeholder="e.g. 5.5" value={formData.rebate} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Redistribution %</label>
                    <input type="number" step="0.01" name="redistribution" className="widget-input" placeholder="e.g. 10" value={formData.redistribution} onChange={handleInputChange} />
                  </div>
                  {/* ------------------- */}

                </div>
                
                <div className="tab-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                  <button className="action-btn" onClick={handleDeleteOrg} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none' }}>
                    Delete Organisation
                  </button>
                  <button className="action-btn primary" onClick={handleSaveInfo}>
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {/* TAB 2: User Management */}
            {activeTab === 'users' && (
              <div className="tab-content">
                <div className="org-users-toolbar">
                  <h3>{users.length} Users</h3>
                  <button 
                    className="action-btn secondary small"
                    onClick={handleAddUserClick}
                  >
                    + Add User
                  </button>
                </div>
                
                {isLoadingUsers ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Loading users...</div>
                ) : (
                  <table className="org-users-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Job Title</th> 
                        <th>Role</th>
                        <th>Status</th>
                        <th style={{textAlign: 'right'}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length > 0 ? (
                        users.map(user => (
                          <tr key={user.id}>
                            <td>{user.name || user.username}</td>
                            <td>{user.email}</td>
                            <td>{user.job_title || '-'}</td> 
                            <td>
                              <span className={`org-badge role-${user.role ? user.role.toLowerCase() : 'user'}`}>
                                {user.role}
                              </span>
                            </td>
                            <td>
                              <span className={`org-badge status-${user.status ? user.status.toLowerCase() : 'active'}`}>
                                {user.status}
                              </span>
                            </td>
                            <td style={{textAlign: 'right'}}>
                              <button 
                                className="org-icon-btn" 
                                title="Edit"
                                onClick={() => handleEditUserClick(user)}
                                style={{ marginRight: '10px' }}
                              >
                                ✏️
                              </button>
                              <button 
                                className="org-icon-btn delete" 
                                title="Delete"
                                onClick={() => handleDeleteUser(user.id, user.name || user.username)}
                                style={{ color: '#dc3545' }}
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" style={{textAlign: 'center', padding: '20px'}}>No users found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SEPARATE USER WIDGET */}
      {showUserWidget && (
        <UserManagementWidget 
          user={selectedUser}
          orgId={organisation.id}
          onClose={() => setShowUserWidget(false)}
          onSave={handleUserWidgetSave}
        />
      )}
    </>
  );
};

export default OrganisationManagement;
